"use client";
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useFormik } from "formik";
import { get, set } from "lodash";
import { ModuleSettingsStore } from "../../../../utils/contentstack/moduleSettingsStore.js";
import { ZodError } from "zod";
import { useState, useRef, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { Modal, Text, MarkdownRenderer, Divider, Button, Popup, AnnouncerModel, Grid, Notification, TextField, InputError, Link } from "@nxt-costco-com/forge-components";
import { FontSize500, SpaceSm, SpaceLg, SpaceXs, ColorGreen500, SpaceXxs, FontSize100, ColorTextLink, FontSize400 } from "@nxt-costco-com/forge-design-tokens";
import { Checkmark } from "@nxt-costco-com/forge-icons/icons/Checkmark";
import { Analytics } from "../../../Analytics/index.js";
import { COMPONENT_NAME, MEMBERSHIP_NUMBER_FIELD_LENGTH } from "../constants.js";
import { getErrorAriaIconAttributes, standardBDFormValidationSchema, standardBCFormValidationSchema, caInputMask, usInputMask } from "../lib.js";
import { getStorageObject, createFetchProxy, splitMarkdownLinks } from "../utils.js";
import { CostcoBox } from "../../../Util/CostcoBox.js";
import GenericImageHandler from "../../../Util/GenericImageHandler.js";
const initialState = {
  isDelayed: true,
  isModalOpen: false,
  isModified: false,
  isPopupOpen: true
};
const EmailSignUpFormUIStandard = ({
  data,
  successRate = 0.9,
  isZipCodeFieldVisible = false,
  apiEndpointData = {},
  authToken = "",
  site = "",
  ...restOfProps
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
  const {
    key_value = [],
    composable_window = [],
    properties = [],
    isUsingMockData = false
  } = data ?? {};
  const [state, setState] = useState(initialState);
  const { current: storageObject } = useRef(
    getStorageObject(COMPONENT_NAME, {
      closedOn: 0,
      isDismissed: false,
      numOfRepeatsUsed: 0
    })
  );
  const msStore = new ModuleSettingsStore({
    composable_window,
    key_value,
    properties
  });
  const {
    titleContent,
    bodyContent,
    emailFieldPlaceholder,
    membershipFieldPlaceholder,
    zipCodeFieldPlaceholder,
    membershipHelpLinkContent,
    privacyPolicyContent,
    contactUsContent,
    primaryButtonContent,
    secondaryButtonContent,
    successContent,
    emailError,
    membershipError,
    zipCodeError,
    apiFailureContent,
    costcoMembershipCardTitle,
    body: costcoMembershipCardBody,
    digitalMembershipCardTitle,
    creditCardTitle,
    buttonLabel,
    errorAriaLabelPrefix
  } = msStore.composable_window ?? {};
  const costcoMembershipCardImage = msStore.getComposableWindowImage(
    "costcoMembershipCardTitle"
  );
  const digitalMembershipCardImage = msStore.getComposableWindowImage(
    "digitalMembershipCardTitle"
  );
  const creditCardImage = msStore.getComposableWindowImage("creditCardTitle");
  const { privacyPolicyLink = "", contactUsLink = "" } = ((_b = (_a = msStore.properties) == null ? void 0 : _a.emailSignUpConfig) == null ? void 0 : _b.expanded) ?? {};
  const {
    showDelayInSec = 3,
    numOfRepeats = Number.MAX_SAFE_INTEGER,
    daysToExpire = 180
  } = ((_d = (_c = msStore.properties) == null ? void 0 : _c.emailSignUpConfig) == null ? void 0 : _d.businessRules) ?? {};
  const PopupContent = ({ handleClose }) => {
    const validate = (values) => {
      const errors = {};
      try {
        const schema = isZipCodeFieldVisible ? standardBDFormValidationSchema : standardBCFormValidationSchema;
        schema.parse(values);
      } catch (error) {
        if (error instanceof ZodError) {
          const formattedErrors = error.flatten();
          if (formattedErrors.fieldErrors.emailAddress) {
            errors.emailAddress = emailError;
          }
          const inputElem = document.getElementById("emailAddressField");
          if (inputElem) inputElem.focus();
          if (formattedErrors.fieldErrors.membershipNumber) {
            errors.membershipNumber = membershipError;
          }
          if (formattedErrors.fieldErrors.zipCode) {
            errors.zipCode = zipCodeError;
          }
        }
      }
      return errors;
    };
    const apiEndpointCall = createFetchProxy(
      { message: "Success.", status: 200 },
      {
        isNative: !isUsingMockData,
        maxDelayInMillis: 1e3,
        successRate
      }
    );
    const submitEmailForm = async ({
      emailAddress,
      zipCode
    }) => {
      if (!Object.keys(apiEndpointData).length) return;
      const {
        endpoint,
        method,
        required_request_headers: headers,
        required_request_parameters: body
      } = apiEndpointData ?? {};
      const listSource = get(
        apiEndpointData,
        "custom_properties.listSource.popover"
      );
      set(
        apiEndpointData,
        "required_request_headers.Authorization",
        `Bearer ${authToken}`
      );
      set(
        apiEndpointData,
        "required_request_parameters.listSource",
        listSource
      );
      set(
        apiEndpointData,
        "required_request_parameters.emailAddress",
        emailAddress
      );
      set(
        apiEndpointData,
        "required_request_parameters.consents.marketing.email.Time",
        (/* @__PURE__ */ new Date()).toISOString()
      );
      if (zipCode) {
        set(
          apiEndpointData,
          "required_request_parameters.zipCode",
          zipCode
        );
      }
      await apiEndpointCall(
        endpoint,
        {
          body: JSON.stringify(body),
          headers,
          method
        }
      );
    };
    const onSubmit = async () => {
      try {
        const { emailAddress, zipCode } = formik.values ?? {};
        await submitEmailForm({
          emailAddress,
          zipCode
        });
        formik.setFieldValue("isSuccessful", true);
      } catch (error) {
        console.error(error);
        formik.setFieldValue("isFailure", true);
      }
    };
    const formik = useFormik({
      initialValues: {
        emailAddress: "",
        isFailure: false,
        isSuccessful: false,
        membershipNumber: "",
        zipCode: ""
      },
      onSubmit,
      validate,
      validateOnChange: false
    });
    const focusSuccessMessage = (elem) => {
      elem == null ? void 0 : elem.focus();
    };
    const handleEmailAddressChange = (event) => {
      formik.setFieldValue("emailAddress", event.target.value);
    };
    const handleMembershipNumberChange = (event) => {
      if (event.target.value.length > MEMBERSHIP_NUMBER_FIELD_LENGTH) {
        return;
      }
      const value = event.target.value.replace(/[^0-9]/g, "");
      formik.setFieldValue("membershipNumber", value);
    };
    const handleZipCodeChange = (event) => {
      const inputMask = (site == null ? void 0 : site.startsWith("CA")) ? caInputMask : usInputMask;
      inputMask.resolve(event.target.value);
      formik.setFieldValue("zipCode", inputMask.value);
    };
    const handleSubmit = (event) => {
      formik.validateForm().then(() => {
        event.preventDefault();
        formik.handleSubmit();
      });
    };
    const errorTextId = `${COMPONENT_NAME}_EmailSignUp_${formik.errors.emailAddress}`;
    const errorIconA11yAttributes = getErrorAriaIconAttributes(errorAriaLabelPrefix);
    const [privacyPolicyPreamble, privacyPolicyLinkText] = splitMarkdownLinks(privacyPolicyContent);
    const [contactUsPreamble, contactUsLinkText, contactUsPostfix] = splitMarkdownLinks(contactUsContent);
    return /* @__PURE__ */ jsxs(Grid, { container: true, "data-testid": "popupContent", hasMargin: false, children: [
      formik.values.isSuccessful ? /* @__PURE__ */ jsxs(
        CostcoBox,
        {
          sx: {
            display: "flex",
            m: SpaceLg,
            ml: SpaceSm,
            mt: SpaceSm
          },
          children: [
            /* @__PURE__ */ jsx(
              Box,
              {
                component: Checkmark,
                sx: { color: ColorGreen500, mr: SpaceXs }
              }
            ),
            /* @__PURE__ */ jsx(
              Text,
              {
                id: "successConfirmation",
                ref: focusSuccessMessage,
                sx: { color: ColorGreen500 },
                tabIndex: -1,
                variant: "t3",
                children: successContent
              }
            )
          ]
        }
      ) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(Grid, { sx: { mb: SpaceSm }, size: { xs: 12 }, children: [
          formik.values.isFailure && !formik.values.isSuccessful ? /* @__PURE__ */ jsx(
            Notification,
            {
              isRole: true,
              message: apiFailureContent,
              severity: "critical",
              sx: { mb: SpaceLg }
            }
          ) : null,
          /* @__PURE__ */ jsx(Text, { children: bodyContent })
        ] }),
        /* @__PURE__ */ jsxs(
          Grid,
          {
            sx: {
              marginTop: SpaceXs
            },
            container: true,
            hasMargin: false,
            size: { xs: 12 },
            children: [
              /* @__PURE__ */ jsxs(Grid, { size: { xs: 8 }, children: [
                /* @__PURE__ */ jsx(
                  TextField,
                  {
                    helperText: /* @__PURE__ */ jsx(
                      InputError,
                      {
                        ariaLabelErrorPrefix: errorAriaLabelPrefix,
                        errorIconA11yAttributes,
                        errorText: formik.errors.emailAddress,
                        hasError: !!formik.errors.emailAddress,
                        errorTextId
                      }
                    ),
                    sx: {
                      marginLeft: SpaceSm,
                      width: "100%"
                    },
                    autocomplete: "email",
                    errorText: formik.errors.emailAddress,
                    id: "emailAddressField",
                    isError: !!formik.errors.emailAddress,
                    isRequired: true,
                    label: emailFieldPlaceholder,
                    onChange: handleEmailAddressChange,
                    size: "small",
                    tabIndex: 0,
                    value: formik.values.emailAddress
                  }
                ),
                isZipCodeFieldVisible ? /* @__PURE__ */ jsx(
                  TextField,
                  {
                    helperText: /* @__PURE__ */ jsxs(
                      Text,
                      {
                        bold: true,
                        color: "error",
                        role: "alert",
                        children: [
                          formik.errors.zipCode,
                          " "
                        ]
                      }
                    ),
                    sx: {
                      "#zipCodeField-helper-text": {
                        marginBottom: 0,
                        marginTop: 0
                      },
                      marginLeft: SpaceSm,
                      marginTop: SpaceLg,
                      paddingBottom: 0,
                      width: "100%"
                    },
                    errorText: formik.errors.zipCode,
                    isError: !!formik.errors.zipCode,
                    isRequired: true,
                    autocomplete: "off",
                    id: "zipCodeField",
                    label: zipCodeFieldPlaceholder,
                    onChange: handleZipCodeChange,
                    value: formik.values.zipCode
                  }
                ) : /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx(
                    TextField,
                    {
                      helperText: /* @__PURE__ */ jsxs(
                        Text,
                        {
                          bold: true,
                          color: "error",
                          role: "alert",
                          children: [
                            formik.errors.membershipNumber,
                            " "
                          ]
                        }
                      ),
                      sx: {
                        "#membershipNumberField-helper-text": {
                          marginBottom: 0,
                          marginTop: 0
                        },
                        marginLeft: SpaceSm,
                        marginTop: SpaceLg,
                        paddingBottom: 0,
                        width: "100%"
                      },
                      errorText: formik.errors.membershipNumber,
                      isError: !!formik.errors.membershipNumber,
                      autocomplete: "off",
                      id: "membershipNumberField",
                      label: membershipFieldPlaceholder,
                      onChange: handleMembershipNumberChange,
                      value: formik.values.membershipNumber
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Analytics,
                    {
                      analyticData: {
                        component: {
                          trackedData: [
                            membershipHelpLinkContent
                          ],
                          type: "nav"
                        }
                      },
                      children: /* @__PURE__ */ jsx(
                        Button,
                        {
                          onClick: () => setState({
                            ...state,
                            isModalOpen: true
                          }),
                          sx: {
                            color: ColorTextLink,
                            fontSize: FontSize100,
                            marginBottom: SpaceSm,
                            marginLeft: SpaceLg,
                            textAlign: "left",
                            marginTop: SpaceXxs,
                            paddingLeft: SpaceXxs
                          },
                          variant: "text-button",
                          children: membershipHelpLinkContent
                        }
                      )
                    }
                  )
                ] })
              ] }),
              /* @__PURE__ */ jsxs(Grid, { size: { xs: 4 }, children: [
                /* @__PURE__ */ jsx(
                  Analytics,
                  {
                    analyticData: {
                      component: {
                        trackedData: [primaryButtonContent]
                      }
                    },
                    children: /* @__PURE__ */ jsx(
                      Button,
                      {
                        sx: {
                          marginBottom: SpaceLg,
                          width: "100%"
                        },
                        onClick: handleSubmit,
                        type: "submit",
                        variant: "primary",
                        children: primaryButtonContent
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx(
                  Analytics,
                  {
                    analyticData: {
                      component: {
                        trackedData: ["Close"]
                      }
                    },
                    children: /* @__PURE__ */ jsx(
                      Button,
                      {
                        onClick: () => {
                          setState({
                            ...state,
                            isModalOpen: false
                          });
                          storageObject == null ? void 0 : storageObject.set(
                            "isDismissed",
                            true
                          );
                          handleClose == null ? void 0 : handleClose();
                          setState({
                            ...state,
                            isPopupOpen: false
                          });
                        },
                        sx: {
                          marginTop: SpaceXxs,
                          width: "100%"
                        },
                        variant: "tertiary",
                        children: secondaryButtonContent
                      }
                    )
                  }
                )
              ] })
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsx(Grid, { container: true, hasMargin: false, size: { xs: 12 }, children: /* @__PURE__ */ jsxs(
        Text,
        {
          sx: {
            fontSize: FontSize100,
            ml: SpaceLg,
            mt: SpaceSm
          },
          children: [
            privacyPolicyLinkText ? privacyPolicyPreamble : null,
            /* @__PURE__ */ jsx(
              Analytics,
              {
                analyticData: {
                  component: {
                    trackedData: ["Privacy Policy"],
                    type: "nav"
                  }
                },
                children: /* @__PURE__ */ jsx(
                  Link,
                  {
                    sx: {
                      fontSize: FontSize100,
                      marginLeft: SpaceXxs
                    },
                    hasExternalLinkWarning: false,
                    href: privacyPolicyLink,
                    underline: "always",
                    children: privacyPolicyLinkText ? privacyPolicyLinkText : privacyPolicyPreamble
                  }
                )
              }
            ),
            contactUsLink ? /* @__PURE__ */ jsxs(Fragment, { children: [
              " ",
              contactUsPreamble,
              /* @__PURE__ */ jsx(
                Analytics,
                {
                  analyticData: {
                    component: {
                      trackedData: ["Contact Us"],
                      type: "nav"
                    }
                  },
                  children: /* @__PURE__ */ jsx(
                    Link,
                    {
                      sx: {
                        fontSize: FontSize100,
                        marginLeft: SpaceXxs
                      },
                      hasExternalLinkWarning: false,
                      href: contactUsLink,
                      underline: "always",
                      children: contactUsLinkText
                    }
                  )
                }
              ),
              contactUsPostfix
            ] }) : null
          ]
        }
      ) })
    ] });
  };
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setState((state2) => ({ ...state2, isDelayed: false }));
      clearTimeout(timeoutId);
    }, showDelayInSec * 1e3);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [showDelayInSec]);
  const isPopupVisible = useCallback(() => {
    const closedOn = (storageObject == null ? void 0 : storageObject.closedOn) ?? 0;
    const isDismissed = (storageObject == null ? void 0 : storageObject.isDismissed) ?? false;
    const numOfRepeatsUsed = (storageObject == null ? void 0 : storageObject.numOfRepeatsUsed) ?? 0;
    if (Date.now() - (closedOn ?? 0) < daysToExpire * 24 * 60 * 60 * 1e3) {
      return false;
    }
    if (!state.isModified) {
      storageObject == null ? void 0 : storageObject.set("numOfRepeatsUsed", (numOfRepeatsUsed ?? 0) + 1);
      setState({ ...state, isModified: true });
    }
    return !state.isDelayed && !isDismissed && numOfRepeatsUsed <= numOfRepeats;
  }, [state, storageObject, numOfRepeats, daysToExpire]);
  const popupStyles = {
    maxWidth: "500px",
    "#popupTitleId": { fontSize: FontSize500, mt: 0 }
  };
  if (!((_e = bodyContent == null ? void 0 : bodyContent.trim) == null ? void 0 : _e.call(bodyContent))) {
    popupStyles["#popupTitleId"].fontSize = FontSize400;
    popupStyles["#popupTitleId"].mt = SpaceXxs;
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      Modal,
      {
        onClose: () => setState({ ...state, isModalOpen: false }),
        isOpen: state.isModalOpen,
        modalTitle: membershipFieldPlaceholder,
        sx: { zIndex: 9999 },
        children: [
          /* @__PURE__ */ jsx(Text, { children: costcoMembershipCardBody }),
          /* @__PURE__ */ jsx(MarkdownRenderer, { markdown: costcoMembershipCardTitle }),
          /* @__PURE__ */ jsx(
            GenericImageHandler,
            {
              sx: {
                height: costcoMembershipCardImage.height,
                width: costcoMembershipCardImage.width
              },
              alt: (_f = msStore.key_value) == null ? void 0 : _f.alttextcostcomembershipcard,
              loading: "lazy",
              src: costcoMembershipCardImage == null ? void 0 : costcoMembershipCardImage.url
            }
          ),
          /* @__PURE__ */ jsx(Divider, {}),
          /* @__PURE__ */ jsx(MarkdownRenderer, { markdown: digitalMembershipCardTitle }),
          /* @__PURE__ */ jsx(
            GenericImageHandler,
            {
              sx: {
                height: digitalMembershipCardImage.height,
                width: digitalMembershipCardImage.width
              },
              alt: (_g = msStore.key_value) == null ? void 0 : _g.alttextdigitalmembershipcard,
              loading: "lazy",
              src: digitalMembershipCardImage == null ? void 0 : digitalMembershipCardImage.url
            }
          ),
          /* @__PURE__ */ jsx(Divider, {}),
          /* @__PURE__ */ jsx(MarkdownRenderer, { markdown: creditCardTitle }),
          /* @__PURE__ */ jsx(
            GenericImageHandler,
            {
              sx: {
                height: creditCardImage.height,
                width: creditCardImage.width
              },
              alt: (_h = msStore.key_value) == null ? void 0 : _h.alttextcreditcard,
              loading: "lazy",
              src: creditCardImage == null ? void 0 : creditCardImage.url
            }
          ),
          /* @__PURE__ */ jsx(Divider, {}),
          /* @__PURE__ */ jsx(
            Button,
            {
              onClick: () => setState({ ...state, isModalOpen: false }),
              sx: { marginLeft: "50%", width: "50%" },
              variant: "primary",
              children: buttonLabel
            }
          )
        ]
      }
    ),
    isPopupVisible() && /* @__PURE__ */ jsx(
      Analytics,
      {
        analyticData: {
          component: {
            identifier: "email-popup",
            type: "ui"
          },
          trackType: "navigation"
        },
        children: /* @__PURE__ */ jsx(
          Popup,
          {
            announcer: /* @__PURE__ */ jsx(
              AnnouncerModel,
              {
                announcerText: `${titleContent} ${(_i = msStore.key_value) == null ? void 0 : _i.emailsignupformannouncertext}`,
                announcerTime: 5e3,
                callback: () => {
                },
                ...restOfProps
              }
            ),
            onClose: () => {
              if (state.isModalOpen) {
                setState({ ...state, isModalOpen: false });
              }
              setState({ ...state, isPopupOpen: false });
            },
            popupAriaLabel: (_j = msStore.key_value) == null ? void 0 : _j.emailsignupformarialabel,
            renderContent: (handleClose) => /* @__PURE__ */ jsx(PopupContent, { handleClose }),
            closeButtonAriaLabel: "Close",
            "data-testid": COMPONENT_NAME,
            isCloseable: true,
            open: state.isPopupOpen,
            popupName: "emailSignUpForm",
            position: "Right",
            sx: popupStyles,
            title: titleContent,
            titleId: "popupTitleId"
          }
        )
      }
    )
  ] });
};
export {
  EmailSignUpFormUIStandard
};
